import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Data export utilities for CSV, JSON, Excel, and PDF formats
 * Implements Requirement 197 AC 4: Data export functionality from every data view
 */

export type ExportFormat = 'csv' | 'json' | 'excel' | 'pdf';

export interface ExportOptions {
  filename: string;
  format: ExportFormat;
  data: unknown[];
  headers?: string[];
}

/**
 * Export data to CSV format
 */
function exportToCSV(data: unknown[], filename: string): void {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filename}.csv`);
}

/**
 * Export data to JSON format
 */
function exportToJSON(data: unknown[], filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
  downloadBlob(blob, `${filename}.json`);
}

/**
 * Export data to Excel format
 */
function exportToExcel(data: unknown[], filename: string): void {
  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  
  // Convert data to worksheet
  const worksheet = XLSX.utils.json_to_sheet(data);
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  
  // Generate Excel file and trigger download
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

/**
 * Export data to PDF format - a simple, one-page report: title, generated
 * timestamp, and a table of the data. Suitable for exec/MBR-style sharing.
 */
function exportToPDF(data: unknown[], filename: string): void {
  const doc = new jsPDF();

  const title = filename.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  doc.setFontSize(16);
  doc.text(title, 14, 18);

  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 25);

  const rows = data as Record<string, unknown>[];
  const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

  autoTable(doc, {
    startY: 30,
    head: [columns],
    body: rows.map((row) => columns.map((col) => formatCellForPDF(row[col]))),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [37, 99, 235] }, // primary-600
    margin: { left: 14, right: 14 },
  });

  doc.save(`${filename}.pdf`);
}

function formatCellForPDF(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Helper function to trigger file download
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Main export function that handles all formats
 */
export function exportData(options: ExportOptions): void {
  const { data, filename, format } = options;
  
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }
  
  try {
    switch (format) {
      case 'csv':
        exportToCSV(data, filename);
        break;
      case 'json':
        exportToJSON(data, filename);
        break;
      case 'excel':
        exportToExcel(data, filename);
        break;
      case 'pdf':
        exportToPDF(data, filename);
        break;
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  } catch (error) {
    console.error('Export failed:', error);
    throw error;
  }
}

/**
 * Export dashboard data with metadata
 */
export function exportDashboardData(
  data: unknown[],
  filename: string,
  format: ExportFormat,
  metadata?: Record<string, unknown>
): void {
  let dataToExport = data;
  
  // Add metadata as first row for CSV/Excel/PDF
  if (metadata && (format === 'csv' || format === 'excel' || format === 'pdf')) {
    const metadataRow = {
      ...metadata,
      exportedAt: new Date().toISOString(),
    };
    dataToExport = [metadataRow, ...data];
  }
  
  // For JSON, wrap data with metadata
  if (metadata && format === 'json') {
    const wrappedData = {
      metadata: {
        ...metadata,
        exportedAt: new Date().toISOString(),
      },
      data,
    };
    const json = JSON.stringify(wrappedData, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
    downloadBlob(blob, `${filename}.json`);
    return;
  }
  
  exportData({ data: dataToExport, filename, format });
}

/**
 * Format data for export by flattening nested objects
 */
export function flattenForExport(data: unknown[]): unknown[] {
  return data.map((item) => flattenObject(item));
}

/**
 * Flatten nested object for CSV/Excel export
 */
function flattenObject(obj: unknown, prefix = ''): Record<string, unknown> {
  if (typeof obj !== 'object' || obj === null) {
    return { [prefix]: obj };
  }
  
  const flattened: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    const newKey = prefix ? `${prefix}.${key}` : key;
    
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(flattened, flattenObject(value, newKey));
    } else if (Array.isArray(value)) {
      flattened[newKey] = JSON.stringify(value);
    } else {
      flattened[newKey] = value;
    }
  }
  
  return flattened;
}
